'use client';

/**
 * ExcursionBookingForm — sidebar booking escursioni.
 *
 * Layout speculare a `BookingSidebar` (boat detail): card sezionate (data,
 * partecipanti, totale, CTA), trust badge, fallback WhatsApp per preventivi.
 *
 * Slot picker → date+ora; tier picker o numero persone in base a pricing_mode;
 * totale dinamico calcolato client-side e ripetuto sul CTA.
 */

import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { priceForDate, slotPriceForDate, tiersForDate } from '@/lib/excursion-seasonal-slots';
import { blockedSeatsForFascia, timeOf, type ExcBlock } from '@/lib/excursion-availability';
import { lastMinutePercent } from '@/lib/pricing/last-minute';
import { resolveExcursionExtras, mandatoryExtraKeys, type ExcursionExtra } from '@/lib/excursion-extras';
import {
  Calendar, Users, Loader2, CheckCircle, AlertCircle,
  Layers, Star, Shield, X,
} from 'lucide-react';
import { useAuth } from '@/app/providers';

interface Slot {
  id: string;
  start_at: string;
  end_at: string;
  capacity: number | null;
  booked_count: number;
  price_override: number | null;
}

interface PricingTier {
  persons: number;
  price: number | string;
  label?: string;
  exclusive?: boolean;
}

interface Props {
  excursionId: string;
  excursionTitle?: string;
  slug: string;
  pricingMode: string;
  price: number;
  maxCapacity: number;
  slots: Slot[];
  pricingTiers?: PricingTier[];
  /** Opzione uso esclusivo: tariffa flat per gruppo intero (con markup applicato lato server).
   *  Se valorizzato (>0) viene mostrato come ulteriore opzione tier con stella. */
  exclusivePrice?: number | null;
  exclusiveLabel?: string | null;
  exclusiveMaxPersons?: number | null;
  /** Markup % cliente (cascade). Aggiunto a vendor_tariff per prezzo finale. Default 12. */
  markupRate?: number;
  /** % della tariffa vendor incassata online ora. Resto al porto. Default 100. */
  depositPercent?: number;
  /** Cliente preautorizza carta per addebito automatico saldo no-show. */
  requiresCardGuarantee?: boolean;
  /** 'derived' → partenze derivate dalle fasce (date+ora scelte dal cliente,
   *  slot materializzato lazy). Altrimenti slot materializzati (prop slots). */
  availabilityMode?: string;
  /** Periodi stagionali (MM-DD) con eventuale prezzo: per mostrare la tariffa
   *  della stagione della data selezionata (allineata al calcolo server). */
  seasonalPeriods?: import('@/lib/excursion-seasonal-slots').SeasonalPeriod[];
  /** Config last-minute (sconto vendor se partenza entro N giorni). */
  lastMinute?: { enabled?: boolean | null; daysBefore?: number | null; percent?: number | null };
  /** Catalogo extra (vendor-defined). Mandatory sempre inclusi; il cliente sceglie gli opzionali. */
  extras?: ExcursionExtra[];
}

export default function ExcursionBookingForm({
  excursionId, excursionTitle, slug, pricingMode, price, maxCapacity, slots, pricingTiers = [],
  exclusivePrice, exclusiveLabel, exclusiveMaxPersons,
  // Default = markup piattaforma globale configurato (10%). Il valore reale
  // per-vendor (excursion_commission_rate ?? commission_rate ?? global) arriva
  // via prop dal chiamante; questo default è solo fallback difensivo.
  markupRate = 10, depositPercent = 100, requiresCardGuarantee = false,
  availabilityMode, seasonalPeriods, lastMinute, extras = [],
}: Props) {
  // Helper: tariffa vendor → prezzo cliente (markup additivo, no rounding nearest-5 client-side).
  const toCustomer = (vendorTariff: number) => Math.round(vendorTariff * (1 + markupRate / 100));
  const { user } = useAuth();
  const isDerived = availabilityMode === 'derived';
  const [selectedSlot, setSelectedSlot] = useState<string>(slots[0]?.id || '');

  // Modello derived: disponibilità derivata dalle fasce (date + orari).
  const [availDates, setAvailDates] = useState<Array<{ date: string; departures: Array<{ start_time: string; remaining: number | null }> }>>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  // Generated (slot materializzati): carico i blocchi per sottrarre i posti
  // bloccati parzialmente (telefonico/iCal) dalla disponibilità mostrata.
  const [genBlocks, setGenBlocks] = useState<ExcBlock[]>([]);
  useEffect(() => {
    if (isDerived) return;
    let alive = true;
    fetch(`/api/excursions/${excursionId}/blocks`, { cache: 'no-store', credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!alive || !j?.success) return;
        const rows = (j.data || []) as Array<{ start_date: string; end_date: string; start_time: string | null; seats_blocked: number | null }>;
        setGenBlocks(rows.map((b) => ({ start_date: String(b.start_date).slice(0, 10), end_date: String(b.end_date).slice(0, 10), start_time: b.start_time, seats_blocked: b.seats_blocked })));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [isDerived, excursionId]);
  useEffect(() => {
    if (!isDerived) return;
    let alive = true;
    fetch(`/api/excursions/${excursionId}/available-dates`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!alive || !j?.success) return;
        const data = (j.data || []) as Array<{ date: string; departures: Array<{ start_time: string; remaining: number | null }> }>;
        setAvailDates(data);
        if (data[0]) { setSelectedDate(data[0].date); setSelectedTime(data[0].departures[0]?.start_time || ''); }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [isDerived, excursionId]);
  const dayDeps = availDates.find((d) => d.date === selectedDate)?.departures || [];

  const slot = useMemo(() => slots.find(s => s.id === selectedSlot) || null, [slots, selectedSlot]);

  // Tier normali (stagionali se la stagione della data ha tariffe proprie, altrimenti
  // listino statico) + eventuale tier "Uso esclusivo" derivato da exclusive_* (sempre in
  // fondo). Markup invisibile al cliente — server applica via engine (e ri-valida il tier
  // scelto contro la stessa fonte, vedi app/api/excursion-bookings/route.ts).
  const tiers = useMemo(() => {
    const d = slot?.start_at ? slot.start_at.slice(0, 10) : selectedDate;
    const md = d ? d.slice(5, 10) : '';
    // Fascia oraria scelta (Stagione→Fascia→prezzo): slot materializzato o orario derived.
    const timeSel = slot?.start_at ? timeOf(slot.start_at) : selectedTime;
    const seasonalTiers = seasonalPeriods && md ? tiersForDate(seasonalPeriods, md, timeSel || undefined) : null;
    const effectivePricingTiers = seasonalTiers && seasonalTiers.length > 0 ? seasonalTiers : pricingTiers;
    const normal = effectivePricingTiers
      .map((t) => ({ ...t, persons: Number(t.persons) || 1, price: Number(t.price) || 0 }))
      .sort((a, b) => a.persons - b.persons || (a.exclusive ? 1 : 0) - (b.exclusive ? 1 : 0));
    const exc = exclusivePrice && Number(exclusivePrice) > 0
      ? [{
          persons: exclusiveMaxPersons || maxCapacity || 1,
          price: Number(exclusivePrice),
          label: exclusiveLabel || 'Uso esclusivo',
          exclusive: true as const,
        }]
      : [];
    return [...normal, ...exc];
  }, [pricingTiers, seasonalPeriods, slot, selectedDate, selectedTime, exclusivePrice, exclusiveLabel, exclusiveMaxPersons, maxCapacity]);

  // Mostra picker tier se modalità tiered O se c'è opzione esclusiva (anche su per_person/per_group).
  const showTierPicker = pricingMode === 'tiered' || tiers.some(t => t.exclusive);
  // Su misura: nessun prezzo, flusso a preventivo (CTA "Richiedi preventivo", totale "Su richiesta").
  const isQuote = pricingMode === 'quote_on_request';

  const [tierKey, setTierKey] = useState<string>(tiers[0] && showTierPicker ? '0' : '');
  useEffect(() => { if (tiers[0] && !tierKey && showTierPicker) setTierKey('0'); }, [tiers, tierKey, showTierPicker]);
  const selectedTier = tierKey ? tiers[Number(tierKey)] : null;

  const [peopleManual, setPeopleManual] = useState(1);
  const people = selectedTier ? selectedTier.persons : peopleManual;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // Prefill se loggato
  useEffect(() => {
    if (user?.name && !name) setName(user.name);
    if (user?.email && !email) setEmail(user.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Extra: mandatory sempre inclusi, gli opzionali li sceglie il cliente.
  const [selectedExtraKeys, setSelectedExtraKeys] = useState<string[]>(() => mandatoryExtraKeys(extras));
  const mandatoryKeys = useMemo(() => mandatoryExtraKeys(extras), [extras]);
  const toggleExtra = (key: string) => {
    if (mandatoryKeys.includes(key)) return;
    setSelectedExtraKeys((prev) => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  // Prezzo stagionale per la data selezionata (derived: selectedDate; slot: start_at).
  // Allinea il display al calcolo server: slot.price_override > FASCIA (mode-aware) >
  // stagione > fisso. Per fascia usa il campo della modalità (per_person/per_group).
  const seasonalBase = useMemo(() => {
    const d = slot?.start_at ? slot.start_at.slice(0, 10) : selectedDate;
    const md = d ? d.slice(5, 10) : '';
    if (!seasonalPeriods || !md) return null;
    const timeSel = slot?.start_at ? timeOf(slot.start_at) : selectedTime;
    const fascia = timeSel ? slotPriceForDate(seasonalPeriods, md, timeSel, pricingMode as 'per_person' | 'per_group' | undefined) : null;
    return fascia ?? priceForDate(seasonalPeriods, md);
  }, [seasonalPeriods, slot, selectedDate, selectedTime, pricingMode]);
  const slotPrice = slot?.price_override ?? seasonalBase ?? price;

  // Extra selezionati → totale vendor (sommato alla base prima del markup, come server).
  const extrasResolved = useMemo(
    () => resolveExcursionExtras(extras, selectedExtraKeys, people),
    [extras, selectedExtraKeys, people],
  );
  // Tariffa vendor TOTALE (netta) = base (tier/persone/gruppo) + extra. Markup applicato per display + split.
  const baseTariff = selectedTier
    ? Number(selectedTier.price)
    : pricingMode === 'per_person' ? slotPrice * people
    : pricingMode === 'per_group' ? slotPrice
    : slotPrice;
  const vendorTariffTotal = baseTariff + extrasResolved.vendorTotal;
  // Last-minute (modello B): sconto € = tariffa vendor × LM%, tolto al cliente.
  // Il markup resta intatto (calcolato sulla tariffa PIENA).
  const lmPct = lastMinutePercent(slot?.start_at ?? selectedDate, lastMinute ?? {});
  const lmDiscount = lmPct > 0 ? Math.round(vendorTariffTotal * lmPct / 100 * 100) / 100 : 0;
  const vendorNet = vendorTariffTotal - lmDiscount;
  const fullTotal = toCustomer(vendorTariffTotal);
  const markupAmount = fullTotal - vendorTariffTotal; // markup su tariffa PIENA (intatto)
  // Prezzo finale cliente = markup pieno + tariffa vendor scontata.
  const total = Math.round((markupAmount + vendorNet) * 100) / 100;
  // Split deposit (online vs porto). Markup intero sempre online.
  const vendorOnline = Math.round(vendorNet * (depositPercent / 100));
  const vendorOffline = vendorNet - vendorOnline;
  const customerOnline = markupAmount + vendorOnline;
  const customerOffline = vendorOffline;
  const hasSplit = depositPercent < 100 && customerOffline > 0;

  // Stato modale riepilogo prima del submit
  const [showSummary, setShowSummary] = useState(false);
  const [phoneEdit, setPhoneEdit] = useState(phone);
  const [messageEdit, setMessageEdit] = useState(message);

  /** Apertura modale riepilogo: auth + minima validazione */
  const openSummary = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?returnUrl=${returnUrl}`;
      return;
    }
    if (!name.trim() || !email.trim()) {
      setError('Email o nome mancanti sul profilo. Aggiorna il profilo prima di prenotare.');
      return;
    }
    setError(null);
    setPhoneEdit(phone);
    setMessageEdit(message);
    setShowSummary(true);
  };

  /** Submit reale chiamato dal modale "Invia richiesta" */
  const confirmAndSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/excursion-bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          excursion_id: excursionId,
          slug,
          slot_id: isDerived ? null : (selectedSlot || null),
          departure_date: isDerived ? selectedDate || null : null,
          departure_time: isDerived ? selectedTime || null : null,
          people,
          guest_name: name.trim(),
          guest_email: email.trim(),
          guest_phone: phoneEdit.trim() || null,
          notes: messageEdit.trim() || null,
          pricing_tier: pricingMode === 'tiered' && selectedTier ? {
            persons: selectedTier.persons,
            price: Number(selectedTier.price),
            label: selectedTier.label || null,
            exclusive: !!selectedTier.exclusive,
          } : null,
          extras: selectedExtraKeys,
          total_amount: total,
        }),
      });
      const j = await res.json();
      if (!res.ok || !j.success) {
        // KYC gate: redirect a wizard verifica
        if (j?.code === 'KYC_PHONE_REQUIRED' || j?.code === 'KYC_DOC_REQUIRED') {
          const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `/profile/kyc?return=${returnTo}`;
          return;
        }
        throw new Error(j.error || j.message || `HTTP ${res.status}`);
      }

      // Fix flow: dopo la creazione (status REQUESTED) avvia SUBITO il pagamento
      // Mollie e redirige al checkout. Prima il booking restava orfano (nessun
      // pagamento) e il cliente vedeva solo "richiesta inviata".
      const bookingId: string | undefined = j.data?.id;
      if (bookingId) {
        try {
          const payRes = await fetch('/api/excursion-payments/create', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ excursion_booking_id: bookingId }),
          });
          const pj = await payRes.json().catch(() => ({}));
          if (payRes.ok && pj?.paymentUrl) {
            window.location.href = pj.paymentUrl as string;
            return;
          }
          // Pagamento simulato (test user) o quote_on_request → conferma diretta.
          if (payRes.ok && pj?.simulated) {
            window.location.href = `/booking/confirmed?excursion_booking_id=${bookingId}`;
            return;
          }
        } catch { /* fallback sotto */ }
      }
      // Fallback: pagamento non avviabile (es. quote_on_request) → messaggio richiesta inviata.
      setShowSummary(false);
      setSuccess(true);
    } catch (err) {
      setError((err as Error).message);
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <div className="p-6 text-center space-y-2" data-cid="excursion-booking-success">
        <CheckCircle className="w-14 h-14 mx-auto text-emerald-500" data-cid="excursion-booking-success-icon" />
        <h3 className="font-black text-slate-900 text-lg" data-cid="excursion-booking-success-title">Richiesta inviata!</h3>
        <p className="text-sm text-slate-600" data-cid="excursion-booking-success-msg">
          L&apos;operatore ti contatterà a breve via email per confermare disponibilità e dettagli.
        </p>
      </div>
    );
  }

  return (
    <form
      id="excursion-booking-form"
      data-cid="excursion-booking-form"
      onSubmit={openSummary}
      className="p-5 space-y-4"
    >
      {/* Sezione DATA / SLOT */}
      <div data-cid="excursion-section-date">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1.5">
          <Calendar size={11} /> Quando
        </p>
        {isDerived ? (
          availDates.length > 0 ? (
            <div className="space-y-2" data-cid="excursion-derived-picker">
              <div className="border border-slate-200 rounded-xl overflow-hidden hover:border-slate-300 transition-colors">
                <select
                  id="excursion-input-date"
                  data-cid="excursion-input-date"
                  value={selectedDate}
                  onChange={(e) => { setSelectedDate(e.target.value); const dd = availDates.find((x) => x.date === e.target.value); setSelectedTime(dd?.departures[0]?.start_time || ''); }}
                  required
                  className="w-full px-3 py-3 text-sm font-semibold text-slate-800 bg-white focus:outline-none cursor-pointer"
                >
                  {availDates.map((d) => {
                    const dt = new Date(`${d.date}T00:00:00`);
                    return <option key={d.date} value={d.date}>{dt.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</option>;
                  })}
                </select>
              </div>
              <div className="flex flex-wrap gap-2" data-cid="excursion-derived-times">
                {dayDeps.map((dep) => {
                  const active = selectedTime === dep.start_time;
                  return (
                    <button
                      key={dep.start_time}
                      type="button"
                      data-cid={`excursion-derived-time-${dep.start_time}`}
                      onClick={() => setSelectedTime(dep.start_time)}
                      className={`px-3 py-2 rounded-lg text-sm font-bold border transition-colors ${active ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-700 border-slate-200 hover:border-sky-300'}`}
                    >
                      {dep.start_time}{dep.remaining != null ? <span className={`ml-1 text-[10px] font-semibold ${active ? 'text-sky-100' : 'text-slate-400'}`}>{dep.remaining} posti</span> : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-800 font-medium" data-cid="excursion-derived-none">
              Nessuna data disponibile al momento: invia comunque la richiesta, l&apos;operatore ti proporrà una soluzione.
            </div>
          )
        ) : slots.length > 0 ? (
          <div className="border border-slate-200 rounded-xl overflow-hidden hover:border-slate-300 transition-colors">
            <select
              id="excursion-input-slot"
              data-cid="excursion-input-slot"
              value={selectedSlot}
              onChange={e => setSelectedSlot(e.target.value)}
              required
              className="w-full px-3 py-3 text-sm font-semibold text-slate-800 bg-white focus:outline-none cursor-pointer"
            >
              {slots.map(s => {
                const blk = blockedSeatsForFascia(genBlocks, new Date(s.start_at).toISOString().slice(0, 10), timeOf(s.start_at));
                // Cap slot.capacity (legale) al bookable (maxCapacity = max_guests ?? max_capacity).
                const cap = (maxCapacity != null && s.capacity != null) ? Math.min(s.capacity, maxCapacity) : s.capacity;
                const remaining = cap != null ? Math.max(cap - s.booked_count - blk, 0) : null;
                const d = new Date(s.start_at);
                return (
                  <option key={s.id} value={s.id} disabled={remaining === 0}>
                    {d.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })}
                    {' · '}
                    {d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                    {remaining != null ? ` · ${remaining} posti` : ''}
                  </option>
                );
              })}
            </select>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-800 font-medium" data-cid="excursion-no-slots-notice">
            Nessuna data programmata: invia comunque la richiesta, l&apos;operatore ti proporrà disponibilità su misura.
          </div>
        )}
      </div>

      {/* Sezione PARTECIPANTI / FASCIA */}
      <div data-cid="excursion-section-people">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1.5">
          {showTierPicker ? <Layers size={11} /> : <Users size={11} />}
          {showTierPicker ? 'Fascia / Persone' : 'Partecipanti'}
        </p>

        {/* per_person SENZA tier esclusivo: input numero classico */}
        {pricingMode === 'per_person' && !showTierPicker && (
          <div className="border border-slate-200 rounded-xl flex items-center px-3 py-2 hover:border-slate-300 transition-colors">
            <Users size={14} className="text-slate-400 mr-2" />
            <input
              id="excursion-input-people"
              data-cid="excursion-input-people"
              type="number"
              min={1}
              max={maxCapacity || 99}
              value={peopleManual}
              onChange={e => setPeopleManual(Math.max(1, Number(e.target.value) || 1))}
              className="flex-1 text-sm font-semibold text-slate-800 bg-transparent focus:outline-none numeric"
              required
            />
            <span className="text-xs text-slate-400">/ {maxCapacity || '∞'} max</span>
          </div>
        )}

        {/* per_group SENZA tier esclusivo: card statica */}
        {pricingMode === 'per_group' && !showTierPicker && (
          <div className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 bg-slate-50">
            Prezzo unico a gruppo (max {maxCapacity || '∞'} persone)
          </div>
        )}

        {/* Su misura (quote): input persone senza prezzo (serve al vendor per il preventivo). */}
        {isQuote && (
          <div className="border border-slate-200 rounded-xl flex items-center px-3 py-2 hover:border-slate-300 transition-colors">
            <Users size={14} className="text-slate-400 mr-2" />
            <input
              data-cid="excursion-input-people-quote"
              type="number"
              min={1}
              max={maxCapacity || 99}
              value={peopleManual}
              onChange={e => setPeopleManual(Math.max(1, Number(e.target.value) || 1))}
              className="flex-1 text-sm font-semibold text-slate-800 bg-transparent focus:outline-none numeric"
              required
            />
            <span className="text-xs text-slate-400">persone · / {maxCapacity || '∞'} max</span>
          </div>
        )}

        {/* Per_person/per_group CON tier esclusivo: card "Standard" + tier picker.
            Tier picker integrato sotto se per_person/per_group → primo item "Standard". */}
        {showTierPicker && pricingMode !== 'tiered' && (
          <div className="space-y-1.5 mb-2">
            <button
              type="button"
              onClick={() => setTierKey('')}
              className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border-2 text-sm transition-all text-left ${
                !tierKey
                  ? 'border-blue-500 bg-blue-50 shadow-sm'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <span className="font-bold text-slate-800 inline-flex items-center gap-2">
                <Users size={12} className="text-slate-500" />
                {pricingMode === 'per_person' ? 'Prenotazione standard' : 'A gruppo'}
              </span>
              <span className="font-black text-slate-900 tabular-nums text-xs">
                {pricingMode === 'per_person' ? `€${toCustomer(price)}/pax` : `€${toCustomer(price)} flat`}
              </span>
            </button>
            {/* Se per_person + selezionato standard, mostra input people */}
            {pricingMode === 'per_person' && !tierKey && (
              <div className="border border-slate-200 rounded-xl flex items-center px-3 py-2 ml-3">
                <Users size={14} className="text-slate-400 mr-2" />
                <input
                  type="number"
                  min={1}
                  max={maxCapacity || 99}
                  value={peopleManual}
                  onChange={e => setPeopleManual(Math.max(1, Number(e.target.value) || 1))}
                  className="flex-1 text-sm font-semibold text-slate-800 bg-transparent focus:outline-none numeric"
                  required
                />
                <span className="text-xs text-slate-400">/ {maxCapacity || '∞'} max</span>
              </div>
            )}
          </div>
        )}

        {/* Tier picker (tier mode oppure tier esclusivo virtuale per per_person/per_group) */}
        {showTierPicker && tiers.length > 0 && (
          <div className="space-y-1.5" data-cid="excursion-tier-picker">
            {tiers.map((t, i) => {
              const isSel = tierKey === String(i);
              // Slot pieno → disabilita anche esclusivo (slot occupato già da altri)
              // Cap slot.capacity al bookable prima di determinare se è pieno.
              const effSlotCap = (maxCapacity != null && slot?.capacity != null) ? Math.min(slot.capacity, maxCapacity) : slot?.capacity ?? null;
              const slotFull = slot != null && effSlotCap != null && (slot.booked_count + blockedSeatsForFascia(genBlocks, new Date(slot.start_at).toISOString().slice(0, 10), timeOf(slot.start_at))) >= effSlotCap;
              return (
                <button
                  key={i}
                  id={`excursion-tier-option-${i}`}
                  data-cid={`excursion-tier-option-${i}`}
                  data-tier-exclusive={t.exclusive ? '1' : '0'}
                  data-tier-persons={t.persons}
                  type="button"
                  disabled={slotFull}
                  onClick={() => setTierKey(String(i))}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border-2 text-sm transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed ${
                    isSel
                      ? t.exclusive
                        ? 'border-amber-500 bg-amber-50 shadow-sm'
                        : 'border-blue-500 bg-blue-50 shadow-sm'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <span className="font-bold text-slate-800 inline-flex items-center gap-1.5">
                    {t.exclusive && <Star size={12} className="text-amber-500 fill-amber-400" />}
                    {t.label || `${t.persons} ${t.persons === 1 ? 'persona' : 'persone'}`}
                    {slotFull && <span className="text-[10px] font-normal text-rose-500 ml-1">(slot pieno)</span>}
                  </span>
                  <span className="font-black text-slate-900 tabular-nums">€{toCustomer(Number(t.price)).toFixed(0)}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Extra esperienza (vendor-defined). Mandatory inclusi (disabilitati), opzionali selezionabili. */}
      {extras.length > 0 && (
        <div className="pt-4 border-t border-slate-100 space-y-2" data-cid="excursion-extras">
          <p className="text-sm font-bold text-slate-800">Extra</p>
          {extras.map((ex) => {
            const on = selectedExtraKeys.includes(ex.key) || ex.mandatory;
            const unit = `€${Number(ex.price).toLocaleString('it-IT', { maximumFractionDigits: 0 })}${ex.price_type === 'per_person' ? '/persona' : ''}`;
            return (
              <label
                key={ex.key}
                data-cid={`excursion-extra-${ex.key}`}
                className={`flex items-start gap-2.5 p-2.5 rounded-xl border cursor-pointer ${on ? 'border-blue-400 bg-blue-50/50' : 'border-slate-200'} ${ex.mandatory ? 'opacity-80 cursor-default' : ''}`}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 accent-blue-600"
                  checked={on}
                  disabled={!!ex.mandatory}
                  onChange={() => toggleExtra(ex.key)}
                />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-slate-800">{ex.label}{ex.mandatory && <span className="text-[10px] text-slate-400 ml-1">(incluso)</span>}</span>
                  {ex.description && <span className="block text-[11px] text-slate-500 leading-snug">{ex.description}</span>}
                </span>
                <span className="text-sm font-bold text-slate-700 whitespace-nowrap">{unit}</span>
              </label>
            );
          })}
        </div>
      )}

      {/* Riga TOTALE + breakdown acconto/saldo se split deposit < 100% */}
      <div className="pt-4 border-t border-slate-100 space-y-2" data-cid="excursion-total-row">
        {lmDiscount > 0 && (
          <div className="flex justify-between items-center text-xs" data-cid="excursion-last-minute-line">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500 text-white font-black uppercase tracking-wider">Last Minute −{lmPct}%</span>
            <span className="text-emerald-600 font-bold">−€{lmDiscount.toLocaleString('it-IT', { maximumFractionDigits: 0 })}</span>
          </div>
        )}
        <div className="flex justify-between items-baseline">
          <span className="text-sm font-bold text-slate-800">Totale</span>
          {isQuote ? (
            <span className="text-lg font-black text-amber-600" data-cid="excursion-total-value">Su richiesta</span>
          ) : (
            <span className="text-2xl font-black text-slate-900 tabular-nums" data-cid="excursion-total-value">
              {lmDiscount > 0 && (
                <span className="text-base font-bold text-slate-400 line-through mr-2">€{fullTotal.toLocaleString('it-IT', { maximumFractionDigits: 0 })}</span>
              )}
              €{total.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
          )}
        </div>
        {!isQuote && hasSplit && (
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-1.5 text-xs" data-cid="excursion-total-split">
            <div className="flex justify-between">
              <span className="text-slate-600">Acconto online ora</span>
              <span className="font-bold text-slate-900 tabular-nums">€{customerOnline.toFixed(0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Saldo al porto</span>
              <span className="font-bold text-slate-900 tabular-nums">€{customerOffline.toFixed(0)}</span>
            </div>
            {requiresCardGuarantee && (
              <p className="text-[10px] text-amber-700 pt-1 border-t border-slate-200">
                ⚡ Carta richiesta a garanzia. Addebito automatico del saldo in caso di no-show.
              </p>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-xs text-red-700 font-medium flex items-start gap-2" data-cid="excursion-form-error">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* CTA prominente — totale ripetuto come in BookingSidebar */}
      <button
        id="excursion-submit-btn"
        data-cid="excursion-submit-btn"
        type="submit"
        disabled={submitting}
        className="w-full py-4 text-white rounded-xl font-bold text-sm uppercase tracking-wider transition-all shadow-lg hover:shadow-xl bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {submitting ? <Loader2 size={16} className="animate-spin" /> : (isQuote ? 'Richiedi preventivo' : 'Prenota ora')}
      </button>

      {/* Trust badge */}
      <div className="flex items-center justify-center gap-2 text-xs text-slate-500 pt-1" data-cid="excursion-trust-badge">
        <Shield className="w-4 h-4" />
        <span>Pagamento sicuro · richiesta gratuita</span>
      </div>

      {/* CTA preventivo personalizzato sostituito da SpecialRequestCTA nella
          sidebar (modale interna). WhatsApp rimosso per evitare bypass piattaforma. */}

      {showSummary && (
        <BookingSummaryModal
          excursionTitle={excursionTitle}
          slot={slot}
          pricingMode={pricingMode}
          people={people}
          selectedTier={selectedTier}
          total={total}
          customerOnline={customerOnline}
          customerOffline={customerOffline}
          hasSplit={hasSplit}
          requiresCardGuarantee={requiresCardGuarantee}
          name={name}
          email={email}
          phone={phoneEdit}
          message={messageEdit}
          onPhoneChange={setPhoneEdit}
          onMessageChange={setMessageEdit}
          submitting={submitting}
          error={error}
          onClose={() => setShowSummary(false)}
          onConfirm={confirmAndSubmit}
        />
      )}
    </form>
  );
}

interface SummaryProps {
  excursionTitle?: string;
  slot: Slot | null;
  pricingMode: string;
  people: number;
  selectedTier: PricingTier | null;
  total: number;
  customerOnline: number;
  customerOffline: number;
  hasSplit: boolean;
  requiresCardGuarantee: boolean;
  name: string;
  email: string;
  phone: string;
  message: string;
  onPhoneChange: (v: string) => void;
  onMessageChange: (v: string) => void;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
}

function BookingSummaryModal(p: SummaryProps) {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') p.onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [p]);
  if (typeof document === 'undefined') return null;
  const slotLabel = p.slot
    ? `${new Date(p.slot.start_at).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} · ${new Date(p.slot.start_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`
    : 'Data da concordare con operatore';

  return createPortal(
    <div
      data-cid="excursion-booking-summary-modal"
      className="fixed inset-0 bg-stone-950/65 backdrop-blur-sm flex items-center justify-center p-4"
      style={{ zIndex: 2147483647 }}
      onClick={p.onClose}
    >
      <div
        className="w-full max-w-xl bg-white rounded-3xl shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header data-cid="excursion-booking-summary-header" className="flex items-start justify-between gap-4 p-5 border-b border-stone-100 sticky top-0 bg-white">
          <div>
            <h2 className="font-display text-xl font-bold text-stone-900 tracking-tight">{p.pricingMode === 'quote_on_request' ? 'Richiesta preventivo' : 'Conferma prenotazione'}</h2>
            {p.excursionTitle && <p className="text-xs text-stone-500 mt-0.5">{p.excursionTitle}</p>}
          </div>
          <button type="button" data-cid="excursion-booking-summary-close" onClick={p.onClose} className="p-1.5 hover:bg-stone-100 rounded-lg" aria-label="Chiudi">
            <X size={18} />
          </button>
        </header>

        <div className="p-5 space-y-5" data-cid="excursion-booking-summary-body">
          <SummaryRow label="Quando" value={slotLabel} icon={Calendar} />
          <SummaryRow
            label={p.pricingMode === 'tiered' ? 'Fascia' : 'Partecipanti'}
            value={p.selectedTier
              ? (p.selectedTier.label || `${p.selectedTier.persons} ${p.selectedTier.persons === 1 ? 'persona' : 'persone'}`)
              : `${p.people} ${p.people === 1 ? 'persona' : 'persone'}`}
            icon={p.pricingMode === 'tiered' ? Layers : Users}
          />
          <SummaryRow label="Nome" value={p.name} />
          <SummaryRow label="Email" value={p.email} />

          <div className="space-y-2">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Telefono (opzionale)</span>
              <input
                type="tel"
                data-cid="excursion-booking-summary-phone"
                value={p.phone}
                onChange={(e) => p.onPhoneChange(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Messaggio (opzionale)</span>
              <textarea
                rows={3}
                data-cid="excursion-booking-summary-message"
                value={p.message}
                onChange={(e) => p.onMessageChange(e.target.value)}
                placeholder="Domande, richieste particolari…"
                className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />
            </label>
          </div>

          <div className="pt-4 border-t border-stone-100 space-y-3">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-bold text-stone-800">Totale</span>
              {p.pricingMode === 'quote_on_request' ? (
                <span className="font-display text-2xl font-bold text-amber-600" data-cid="excursion-booking-summary-total">Su richiesta</span>
              ) : (
                <span className="font-display text-3xl font-bold text-stone-900 tabular-nums" data-cid="excursion-booking-summary-total">
                  €{p.total.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
              )}
            </div>
            {p.pricingMode !== 'quote_on_request' && p.hasSplit && (
              <div className="rounded-xl bg-amber-50/60 border border-amber-200 p-3 space-y-2 text-xs" data-cid="excursion-booking-summary-split">
                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-800">Pagamento split</p>
                <div className="flex justify-between">
                  <span className="text-stone-700">Paghi online ora</span>
                  <span className="font-black text-stone-900 tabular-nums">€{p.customerOnline.toFixed(0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-700">Saldo al porto, in giornata</span>
                  <span className="font-black text-stone-900 tabular-nums">€{p.customerOffline.toFixed(0)}</span>
                </div>
                {p.requiresCardGuarantee && (
                  <p className="text-[10px] text-amber-900 pt-2 border-t border-amber-200">
                    ⚡ Carta richiesta a garanzia: in caso di no-show addebitiamo automaticamente il saldo.
                  </p>
                )}
              </div>
            )}
          </div>

          {p.error && (
            <div className="flex items-start gap-2 text-[12px] text-rose-700 bg-rose-50 border border-rose-100 rounded-xl p-3">
              <AlertCircle size={14} className="mt-px shrink-0" /> {p.error}
            </div>
          )}

          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              data-cid="excursion-booking-summary-cancel-btn"
              onClick={p.onClose}
              className="px-4 py-2.5 rounded-full text-sm font-bold text-stone-700 hover:bg-stone-100"
            >
              Annulla
            </button>
            <button
              type="button"
              data-cid="excursion-booking-summary-confirm-btn"
              disabled={p.submitting}
              onClick={p.onConfirm}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-stone-900 text-white text-sm font-bold hover:bg-stone-800 disabled:opacity-50"
            >
              {p.submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
              Invia richiesta
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function SummaryRow({ label, value, icon: Icon }: { label: string; value: string; icon?: React.ElementType }) {
  return (
    <div className="flex items-start gap-3">
      {Icon && <Icon size={14} className="text-stone-400 mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wider text-stone-500">{label}</p>
        <p className="text-sm font-semibold text-stone-900 truncate">{value}</p>
      </div>
    </div>
  );
}

const inputClass = 'w-full px-3 py-2 border border-slate-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400';

function Field({ id, icon: Icon, label, children }: { id?: string; icon?: React.ElementType; label: string; children: React.ReactNode }) {
  return (
    <label id={id} data-cid={id} className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
        {Icon && <Icon size={11} />} {label}
      </span>
      {children}
    </label>
  );
}
